import { use } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Reusable input field component with Tailwind styling and error message display.
 * * @param {object} props
 * @param {string} props.label - The text for the input label.
 * @param {string} props.name - The HTML name attribute.
 * @param {string} [props.type='text'] - The input type (text, email, password, etc.).
 * @param {string} [props.placeholder] - The input placeholder text.
 * @param {boolean} [props.required=false] - Whether the input is required.
 * @param {string} [props.error] - Optional error message to display below the input.
 * @param {function} [props.onChange] - The function to call on input change.
 * @param {string} [props.value] - The current value of the input.
 */
export default function InputField({
    label,
    name,
    type = 'text',
    placeholder,
    required = false,
    error,
    onChange,
    value
}) {

    const { t } = useTranslation();
    // Determine border color based on error state
    const borderColorClass = error
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' // Red if error
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'; // Default blue focus

    return (
        <div>
            <label htmlFor={name} className="block text-sm font-medium text-gray-700">
                {label}
                {required
                    ? <span className="text-red-500 ml-1">*</span>
                    : <span className="text-gray-500 ml-1">{t("input_field_optional")}</span>
                }
            </label>

            <input
                id={name}
                name={name}
                type={type}
                placeholder={placeholder}
                required={required}
                onChange={onChange}
                value={value}

                className={`
          mt-1 block w-full px-3 py-2 
          border ${borderColorClass} 
          rounded-md shadow-sm 
          placeholder-gray-400 
          focus:outline-none focus:ring-1 
          sm:text-sm
        `}
            />

            {/* Conditional Error Message */}
            {error && (
                <p className="mt-1 text-sm text-red-600">
                    {error}
                </p>
            )}
        </div>
    );
}